import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ColorPickerDirective } from 'ngx-color-picker';

@Component({
    selector: 'app-editor-input-color',
    templateUrl: './editor-input-color.component.html',
    styleUrls: ['./editor-input-color.component.css'],
    standalone: false,
})
export class EditorInputColorComponent {
    @ViewChild('alphaInput') alphaInput: ElementRef;
    @ViewChild('colorInput') colorInput: ElementRef;
    @ViewChild(ColorPickerDirective) colorPickerDirective: ColorPickerDirective;

    @Input() name: string;
    @Input() value: string;
    @Input() endIcon: string;
    @Input() light: boolean = false;
    @Input() colorBoxWidth: string = '50%';
    @Input() isReadonly: boolean = false;

    @Output() onIconClick = new EventEmitter<void>();
    @Output() onInputChange = new EventEmitter<string>();

    color: string;
    colorPickerParentElement: HTMLElement;
    colorPickerShow = false;
    alpha: number = 100;
    focus: boolean = false;
    ignoredElements: HTMLElement[] = [];

    constructor(private cdr: ChangeDetectorRef) {}

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent) {
        if (this.focus) {
            this.updateAlpha();
        }
    }

    ngOnInit(): void {
        this.color = this.value ? this.value : '#131B23';
        this.cdr.detectChanges();
    }

    ngAfterViewInit(): void {
        this.updateAlpha();
        this.color = this.color.substring(0, 7);
        if (this.colorInput) {
            this.ignoredElements = [this.colorInput.nativeElement];
        }
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value) {
            this.color = this.value;
            if (this.color?.length === 9 && this.color[0] === '#') {
                const alphaHex = this.color.slice(-2);
                const alphaDecimal = parseInt(alphaHex, 16);
                const alphaPercentage = (alphaDecimal / 255) * 100;
                this.alpha = Number(alphaPercentage.toFixed(0));
                this.color = this.color.substring(0, 7);
            } else {
                this.alpha = 100;
            }
            this.updateAlpha();
            this.cdr.detectChanges();
        }
    }

    inputFocus() {
        this.focus = true;
    }

    inputBlur() {
        this.updateAlpha();
        this.focus = false;
    }

    colorChange(color) {
        this.updateAlpha(color);
    }

    colorClick() {
        if (this.colorPickerShow) {
            this.colorPickerDirective?.closeDialog();
        } else {
            this.colorPickerDirective?.openDialog();
        }
    }

    onPickerStateChange(state: boolean) {
        this.colorPickerShow = state;
    }

    minusButtonClick() {
        this.onIconClick.emit();
    }

    alphaChanged(value: string) {
        if (!this.alphaInput) return;
        const alaphaNumber = Number(value);
        if (!alaphaNumber && alaphaNumber !== 0) return;
        if (alaphaNumber < 0) {
            this.alpha = 0;
            this.alphaInput.nativeElement.value = '0';
        } else if (alaphaNumber > 100) {
            this.alpha = 100;
            this.alphaInput.nativeElement.value = '100';
        }
    }

    updateAlpha(color = '') {
        if (!this.alphaInput) return;
        if (color) this.color = color;

        if (this.alpha) {
            const numericValue = parseInt(String(this.alpha), 10);
            if (numericValue > 100) {
                this.alpha = 100;
                this.alphaInput.nativeElement.value = this.alpha;
            } else if (numericValue < 0) {
                this.alpha = 0;
                this.alphaInput.nativeElement.value = this.alpha;
            } else {
                this.alpha = numericValue;
                this.alphaInput.nativeElement.value = numericValue;
            }
        } else {
            this.alpha = 0;
            this.alphaInput.nativeElement.value = this.alpha;
        }

        const alphaHex = Math.floor((Number(this.alpha) / 100) * 255)
            .toString(16)
            .padStart(2, '0');

        if (this.color.length <= 8) {
            this.color = this.color + alphaHex;
        } else {
            this.color = this.color.slice(0, 7) + alphaHex;
        }

        this.colorInput.nativeElement.style.backgroundColor = this.color;
        this.cdr.detectChanges();
        this.onInputChange.emit(this.color);
    }

    colorPickerClose() {
        const element = this.colorPickerParentElement.querySelector('.hex-text > div > input') as any;
        if (!element) return;
        this.color = element.value;

        if (element.value?.length === 9 && element.value[0] === '#') {
            const alphaHex8 = this.color.slice(-2);
            const alphaDecimal = parseInt(alphaHex8, 16);
            const alphaPercentage = (alphaDecimal / 255) * 100;
            this.alpha = Math.floor(alphaPercentage);
            this.alphaInput.nativeElement.value = this.alpha;
        } else {
            const alphaHex = Math.floor((Number(this.alpha.toFixed(0)) / 100) * 255)
                .toString(16)
                .padStart(2, '0');

            this.color = this.color + alphaHex;
        }

        this.colorInput.nativeElement.style.backgroundColor = this.color;

        this.onInputChange.emit(this.color);
    }

    colorPickerOpen() {
        this.colorPickerParentElement = this.colorInput.nativeElement.parentElement;
        const hexInput = this.colorPickerParentElement?.querySelector('.hex-text > div > input') as HTMLInputElement;
        if (hexInput && this.color) {
            hexInput.value = this.color.substring(0, 7);
            hexInput.dispatchEvent(new Event('input', { bubbles: true }));
            hexInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}
